/**
 * The home page, faithful to the authoritative Claude Design `Site Institucional v2.dc.html` (D624): the hero with the
 * animated route and the live trip card, the product picker, the proof band, "how it works" beside a phone, the two
 * audience cards, the frequent questions, the served cities on the coverage map, and the download band with a real QR
 * code. Every string is the market's injected `SiteContent`; every destination a pack `SiteTarget`; every colour a
 * `@vello/tokens` variable. Positions that change at runtime (the map pin) go through CSS custom properties set on the
 * element, never an inline `style` prop.
 *
 * Motion follows the design (owner rule D546 #5): the inline layer transitions keep playing under reduced motion; only
 * the `vello-fx-*` effects (the live dot) are stilled. Sections reveal as they scroll in only where
 * `IntersectionObserver` exists — without it everything is simply shown.
 */
import { type ReactElement, type RefObject, useEffect, useId, useMemo, useRef, useState } from 'react';
import qrcode from 'qrcode-generator';
import { EFFECT_CLASS } from '@vello/tokens';
import type { SiteContent, SiteProduct } from '@vello/web-entry';
import { resolveSiteTarget } from '../src/site-links.js';
import { SiteLinkTo, useSiteNav } from './SiteChrome';
import { ArtSlot, StoreBadges } from './SitePages';
import { Icon } from './icons';

/** Reveal the `[data-reveal]` blocks as they scroll into view (design v2), when the browser can tell. */
function useReveal(root: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = root.current;
    if (el === null || typeof window === 'undefined' || !('IntersectionObserver' in window)) return undefined;
    const blocks = Array.from(el.querySelectorAll<HTMLElement>('[data-reveal]'));
    for (const b of blocks) b.classList.add('reveal-pending');
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          e.target.classList.remove('reveal-pending');
          io.unobserve(e.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
    );
    for (const b of blocks) io.observe(b);
    return () => io.disconnect();
  }, [root]);
}

/** A repeating timer that can be re-armed (a click restarts the count) and paused. */
function useTicker(ms: number, onTick: () => void, paused: boolean): () => void {
  const tick = useRef(onTick);
  tick.current = onTick;
  const [epoch, setEpoch] = useState(0);
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => tick.current(), ms);
    return () => clearInterval(id);
  }, [ms, paused, epoch]);
  return () => setEpoch((e) => e + 1);
}

// ---- Hero ---------------------------------------------------------------------------------------------------------------

function Hero({ content: c }: { content: SiteContent }): ReactElement {
  const phases = c.hero.phases ?? [];
  const [phase, setPhase] = useState(0);
  const [cardShown, setCardShown] = useState(true);
  const swap = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const nextRef = useRef<() => void>(() => undefined);
  const next = (): void => {
    if (phases.length < 2) return;
    setCardShown(false);
    clearTimeout(swap.current);
    swap.current = setTimeout(() => {
      setPhase((p) => (p + 1) % phases.length);
      setCardShown(true);
    }, 170);
  };
  nextRef.current = next;
  useTicker(3400, next, phases.length < 2);
  useEffect(() => {
    // The first phase change comes sooner, as in the design.
    const kick = setTimeout(() => nextRef.current(), 1400);
    return () => {
      clearTimeout(kick);
      clearTimeout(swap.current);
    };
  }, []);

  const ph = phases[phase];
  const drawn = phases.length === 0 || phase > 0;
  const live = ph === undefined || ph.live;
  return (
    <section className="home-hero">
      <div className="home-hero-copy" data-reveal="">
        <h1 className="home-hero-title">{c.hero.title}</h1>
        <p className="home-hero-sub">{c.hero.subtitle}</p>
        <div className="hero-cta">
          <SiteLinkTo target="download#rider" className="btn btn--primary btn--lg">{c.hero.ctaPrimary}</SiteLinkTo>
          <SiteLinkTo target="drivers" className="btn btn--secondary btn--lg">{c.hero.ctaSecondary}</SiteLinkTo>
        </div>
        <div className="store-row">
          <StoreBadges stores={c.stores?.['rider']} appStore={c.hero.appStore} googlePlay={c.hero.googlePlay} className="store-badge" fallback="download#rider" />
          <span className="store-note">{c.hero.storeNote}</span>
        </div>
      </div>
      <div className="home-map" aria-hidden="true">
        <svg className="home-map-svg" viewBox="0 0 600 520" preserveAspectRatio="none" focusable="false">
          <path className={drawn ? 'home-route is-drawn' : 'home-route'} d="M110 395 C 190 300, 250 360, 320 250 S 440 150, 490 118" />
        </svg>
        <span className="home-pin home-pin--origin" />
        <span className={drawn ? 'home-pin home-pin--dest is-shown' : 'home-pin home-pin--dest'} />
        <div className="home-trip">
          <span className={live ? `home-trip-dot is-live ${EFFECT_CLASS.liveDot}` : 'home-trip-dot'} />
          <div className={cardShown ? 'home-trip-copy' : 'home-trip-copy is-swapping'}>
            <span className="home-trip-title">{ph?.title ?? c.hero.cardEta}</span>
            {ph !== undefined && <span className="home-trip-sub">{ph.sub}</span>}
          </div>
          <span className="home-trip-price tnum">{c.hero.cardPrice}</span>
        </div>
      </div>
    </section>
  );
}

// ---- Product picker -----------------------------------------------------------------------------------------------------

function ProductPicker({ products }: { products: readonly SiteProduct[] }): ReactElement | null {
  const [selected, setSelected] = useState(0);
  const [shown, setShown] = useState(true);
  const swap = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const detailId = useId();
  useEffect(() => () => clearTimeout(swap.current), []);
  if (products.length === 0) return null;
  const withDetail = products.every((p) => p.detail !== undefined);

  const pick = (i: number): void => {
    if (i === selected) return;
    setShown(false);
    clearTimeout(swap.current);
    swap.current = setTimeout(() => {
      setSelected(i);
      setShown(true);
    }, 180);
  };

  const card = (p: SiteProduct): ReactElement => (
    <>
      <span className="product-icon"><Icon name={p.icon} size={28} /></span>
      <span className="product-title">{p.title}</span>
      <span className="product-body">{p.body}</span>
    </>
  );
  const detail = products[selected]?.detail;
  return (
    <section id="products" className="home-section home-products">
      <div className="home-product-cards" data-reveal="">
        {products.map((p, i) =>
          withDetail ? (
            <button
              key={p.title}
              type="button"
              className={i === selected ? 'product-card product-pick is-selected' : 'product-card product-pick'}
              aria-pressed={i === selected}
              aria-controls={detailId}
              onClick={() => pick(i)}
            >
              {card(p)}
            </button>
          ) : p.target !== undefined ? (
            <SiteLinkTo key={p.title} target={p.target} className="product-card product-card--link">{card(p)}</SiteLinkTo>
          ) : (
            <div key={p.title} className="product-card">{card(p)}</div>
          ),
        )}
      </div>
      {withDetail && detail !== undefined && (
        <div id={detailId} className="product-detail" data-reveal="" aria-live="polite">
          <div className={shown ? 'product-detail-copy' : 'product-detail-copy is-swapping'}>
            <span className="eyebrow">{detail.kicker}</span>
            <h2 className="product-detail-title">{detail.title}</h2>
            <p className="product-detail-body">{detail.body}</p>
            <ul className="check-list product-detail-bullets">
              {detail.bullets.map((b) => (
                <li key={b} className="check-item">
                  <span className="check-icon"><Icon name="check-circle" size={18} /></span>
                  {b}
                </li>
              ))}
            </ul>
            <SiteLinkTo target={detail.cta.target} className="arrow-link">
              {detail.cta.label}
              <Icon name="arrow-right" size={16} />
            </SiteLinkTo>
          </div>
          <ArtSlot className={shown ? 'product-detail-art' : 'product-detail-art is-swapping'} />
        </div>
      )}
    </section>
  );
}

// ---- How it works -------------------------------------------------------------------------------------------------------

function HowItWorks({ how }: { how: NonNullable<SiteContent['howItWorks']> }): ReactElement {
  const total = how.steps.length;
  const [step, setStep] = useState(0);
  const [shotShown, setShotShown] = useState(true);
  const [paused, setPaused] = useState(false);
  const swap = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(swap.current), []);
  const go = (n: number): void => {
    setShotShown(false);
    clearTimeout(swap.current);
    swap.current = setTimeout(() => {
      setStep(n);
      setShotShown(true);
    }, 200);
  };
  const rearm = useTicker(3000, () => go(step >= total - 1 ? 0 : step + 1), paused || total < 2);
  const choose = (n: number): void => {
    rearm();
    go(n);
  };
  const current = how.steps[step];
  return (
    <section id="how" className="home-section home-how" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div className="home-how-copy" data-reveal="">
        <div className="home-head">
          <span className="eyebrow">{how.eyebrow}</span>
          <h2 className="home-h2">{how.title}</h2>
        </div>
        <div className="stepper" aria-hidden="true">
          {how.steps.map((s, i) => (
            <span key={s.title} className="stepper-part">
              {i > 0 && <span className={i <= step ? 'stepper-line is-filled' : 'stepper-line'} />}
              <span className={i <= step ? 'stepper-dot is-done' : 'stepper-dot'}>{i + 1}</span>
            </span>
          ))}
        </div>
        <ol className="how-steps">
          {how.steps.map((s, i) => (
            <li key={s.title}>
              <button type="button" className={i === step ? 'how-step is-current' : 'how-step'} aria-current={i === step ? 'step' : undefined} onClick={() => choose(i)}>
                <span className="how-step-title">{s.title}</span>
                <span className="how-step-body">{s.body}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
      <div className="phone-wrap" data-reveal="" aria-hidden="true">
        <div className="phone">
          <div className="phone-screen">
            <span className="phone-notch" />
            <div className={shotShown ? 'phone-shot' : 'phone-shot is-swapping'}>
              <span className="phone-shot-num tnum">{String(step + 1).padStart(2, '0')}</span>
              {current !== undefined && <span className="phone-shot-label">{current.title}</span>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---- Frequent questions -------------------------------------------------------------------------------------------------

function Faq({ faq, cities, locale }: { faq: NonNullable<SiteContent['faq']>; cities: readonly string[]; locale: string }): ReactElement {
  const [open, setOpen] = useState(0);
  const baseId = useId();
  const items = useMemo(() => {
    let list = '';
    try {
      list = new Intl.ListFormat(locale, { type: 'conjunction' }).format(cities);
    } catch {
      list = cities.join(', ');
    }
    // A question that names the served cities is left out while there are none to name.
    return faq.items.filter((i) => !i.answer.includes('{cities}') || cities.length > 0).map((i) => ({ ...i, answer: i.answer.replace('{cities}', list) }));
  }, [faq.items, cities, locale]);
  return (
    <section id="faq" className="home-section home-faq">
      <div className="home-faq-intro" data-reveal="">
        <span className="eyebrow">{faq.eyebrow}</span>
        <h2 className="home-h2">{faq.title}</h2>
        <p className="home-faq-body">{faq.body}</p>
        <SiteLinkTo target={faq.cta.target} className="btn btn--secondary">{faq.cta.label}</SiteLinkTo>
      </div>
      <div className="faq2" data-reveal="">
        {items.map((item, i) => {
          const isOpen = open === i;
          const id = `${baseId}-${i}`;
          return (
            <div key={item.question} className={isOpen ? 'faq2-item is-open' : 'faq2-item'}>
              <h3 className="faq2-h">
                <button type="button" className="faq2-q" aria-expanded={isOpen} aria-controls={id} onClick={() => setOpen(isOpen ? -1 : i)}>
                  <span className="faq2-q-text">{item.question}</span>
                  <span className="faq2-chevron"><Icon name="chevron-down" size={18} /></span>
                </button>
              </h3>
              <div id={id} className="faq2-a" role="region" aria-label={item.question}>
                <div className="faq2-a-inner">
                  <p>{item.answer}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ---- Cities -------------------------------------------------------------------------------------------------------------

function Cities({ cities }: { cities: SiteContent['cities'] }): ReactElement | null {
  const [selected, setSelected] = useState(0);
  const pin = useRef<HTMLDivElement>(null);
  const name = cities.names[selected];
  const place = name !== undefined ? cities.places?.[name] : undefined;
  useEffect(() => {
    const el = pin.current;
    if (el === null || place === undefined) return;
    el.style.setProperty('--pin-x', `${place.x}%`);
    el.style.setProperty('--pin-y', `${place.y}%`);
  }, [place]);
  if (cities.names.length === 0) return null;
  return (
    <section id="cities" className="home-section home-cities">
      <div className="home-cities-head" data-reveal="">
        <h2 className="cities-title">{cities.title}</h2>
        {cities.subtitle !== undefined && <span className="home-cities-sub">{cities.subtitle}</span>}
      </div>
      <div className="cities-list" data-reveal="">
        {cities.names.map((n, i) => (
          <button
            key={n}
            type="button"
            className={i === selected ? 'city-chip is-selected' : 'city-chip'}
            aria-pressed={i === selected}
            onMouseEnter={() => setSelected(i)}
            onFocus={() => setSelected(i)}
            onClick={() => setSelected(i)}
          >
            {n}
          </button>
        ))}
        {cities.more !== '' && <span className="city-more">{cities.more}</span>}
      </div>
      {cities.places !== undefined && (
        <div className="home-cities-map" data-reveal="" aria-hidden="true">
          {place !== undefined && name !== undefined && (
            <div className="city-pin" ref={pin}>
              <span className="city-pin-card">
                <span className="city-pin-name">{name}</span>
                <span className="city-pin-info">{place.info}</span>
              </span>
              <span className={`city-pin-dot ${EFFECT_CLASS.liveDot}`} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---- Download band with a real QR code ----------------------------------------------------------------------------------

/** A QR code of an address, drawn as one SVG path in the current text colour. Undefined when it cannot be encoded. */
function QrCode({ value, label }: { value: string; label: string }): ReactElement | null {
  const d = useMemo(() => {
    try {
      const qr = qrcode(0, 'M');
      qr.addData(value);
      qr.make();
      const n = qr.getModuleCount();
      let path = '';
      for (let r = 0; r < n; r += 1) for (let col = 0; col < n; col += 1) if (qr.isDark(r, col)) path += `M${col + 2} ${r + 2}h1v1h-1z`;
      return { path, size: n + 4 };
    } catch {
      return undefined;
    }
  }, [value]);
  if (d === undefined) return null;
  return (
    <svg className="qr" viewBox={`0 0 ${d.size} ${d.size}`} role="img" aria-label={label} shapeRendering="crispEdges">
      <path d={d.path} />
    </svg>
  );
}

function DownloadBand({ content: c }: { content: SiteContent }): ReactElement {
  const { hrefOf } = useSiteNav();
  const href = hrefOf(resolveSiteTarget('download'));
  const absolute = href !== undefined && typeof window !== 'undefined' ? new URL(href, window.location.origin).toString() : undefined;
  return (
    <section id="download" className="home-section">
      <div className="download" data-reveal="">
        <div className="download-copy">
          <h2 className="download-title">{c.download.title}</h2>
          <p className="download-body">{c.download.body}</p>
          <div className="download-badges">
            {c.download.cta !== undefined ? (
              <SiteLinkTo target={c.download.cta.target} className="btn btn--primary">{c.download.cta.label}</SiteLinkTo>
            ) : (
              <StoreBadges stores={c.stores?.['rider']} appStore={c.download.appStore} googlePlay={c.download.googlePlay} className="btn btn--primary btn--sm" fallback="download" />
            )}
          </div>
        </div>
        {absolute !== undefined ? (
          <SiteLinkTo target="download" className="download-qr">
            <QrCode value={absolute} label={c.download.title} />
          </SiteLinkTo>
        ) : null}
      </div>
    </section>
  );
}

// ---- The page -----------------------------------------------------------------------------------------------------------

export function HomePage({ content: c }: { content: SiteContent }): ReactElement {
  const { locale } = useSiteNav();
  const root = useRef<HTMLDivElement>(null);
  useReveal(root);
  return (
    <div ref={root} className="home">
      <Hero content={c} />
      <ProductPicker products={c.products} />

      {c.stats.length > 0 && (
        <section className="home-section">
          <div className="stats" data-reveal="">
            {c.stats.map((s) => (
              <div key={s.label} className="stat">
                <div className="stat-value tnum">{s.value}</div>
                <span className="stat-bar" />
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {c.howItWorks !== undefined && <HowItWorks how={c.howItWorks} />}

      {c.mirror.length > 0 && (
        <section className="home-section home-audience">
          {c.mirror.map((m, i) => (
            <SiteLinkTo key={m.title} target={m.target ?? (i === 0 ? 'download#rider' : 'drivers')} className="audience-card">
              <span className="eyebrow">{m.eyebrow}</span>
              <span className="audience-title">{m.title}</span>
              <span className="audience-body">{m.body}</span>
              <span className="arrow-link">
                {m.link}
                <Icon name="arrow-right" size={16} />
              </span>
              <ArtSlot className="audience-art" />
            </SiteLinkTo>
          ))}
        </section>
      )}

      {c.faq !== undefined && <Faq faq={c.faq} cities={c.cities.names} locale={locale} />}
      <Cities cities={c.cities} />
      <DownloadBand content={c} />
    </div>
  );
}
